import { useUser } from "@clerk/clerk-expo";
import ButtonCart from "components/cart/ButtonCart";
import SiteItem from "components/sites/SiteItem";
import { Box, Button, HStack, Typography, VStack } from "components/ui";
import { Link } from "expo-router";
import {
	ArrowRight,
	ScrollText,
	ShoppingCart,
	Settings,
	Droplets,
	Fan,
	Wind,
	Brush,
	RotateCw,
	Truck,
	Home,
	Trees,
	ChevronRight,
	Calendar,
	FlaskConical,
	Package,
	Shield,
	Trash2,
	UtensilsCrossed,
	Sparkles,
	Wrench,
	GraduationCap,
	MapPin,
	Leaf,
	Users,
	Building,
	Award,
	Handshake,
	Heart,
	School,
	Dumbbell,
	Hospital,
	Pickaxe,
	Store,
	Trophy,
	Plane,
} from "lucide-react-native";
import React, { memo } from "react";
import { ActivityIndicator } from "react-native";
import { useGetArticleQuery, useGetCategoryQuery, useGetOrderQuery, useGetSitesQuery } from "store/abcoApi";
import { servicesNavigationItems, sustainabilityNavigationItems, aboutUsNavigationItems } from "components/navigation/navigationData";

interface MegaMenuContentProps {
	hoveredNavItem: string;
	onCloseMegamenu: () => void;
}

const MegaMenuContent = ({ hoveredNavItem, onCloseMegamenu }: MegaMenuContentProps) => {
	const { isSignedIn, user } = useUser();

	const shouldSkip = !isSignedIn || (user?.publicMetadata && Object.keys(user.publicMetadata).length === 0);

	let content = null;
	const {
		data: orderData,
		isLoading: isLoadingOrderData,
		isFetching: isFetchingOrderData,
	} = useGetOrderQuery(
		{
			pageNumber: 1,
			pageSize: 5,
			sortOrder: "desc",
		},
		{
			skip: shouldSkip,
		},
	);
	const {
		data: siteData,
		isLoading: isLoadingSiteData,
		isFetching: isFetchingSiteData,
	} = useGetSitesQuery(
		{
			pageNumber: 1,
			sortField: "createdAt",
			// @ts-ignore
			sortDirection: "desc",
			pageSize: 5,
			// groupIdCollection: site.map((s) => s.id),
		},
		{
			skip: shouldSkip,
		},
	);
	const {
		data: articleData,
		isLoading: isLoadingArticleData,
		isFetching: isFetchingArticleData,
	} = useGetArticleQuery({
		pageSize: 3,
		pageNumber: 1,
	});
	const { data: equipmentCategoryData, isLoading: isLoadingEquipmentCategoryData, isFetching: isFetchingEquipmentCategoryData } = useGetCategoryQuery();

	const filteredEquipmentCategoryData = equipmentCategoryData?.filter((item: any) => item.categoryType === "Equipment");
	const filteredProductsCategoryData = equipmentCategoryData?.filter((item: any) => item.categoryType === "Products");

	const MemoizedSiteItem = memo(SiteItem);

	const MenuLink = ({ href, children, style, variant }: { href: string; children: React.ReactNode; style?: any; variant?: "primary" | "secondary" | "primary-light" }) => {
		return (
			<Link href={href} asChild>
				<Button variant={variant} onPress={onCloseMegamenu} sx={{ alignSelf: "flex-start", ...style }}>
					{children}
				</Button>
			</Link>
		);
	};

	const getEquipmentCategoryIcon = (categoryName: string) => {
		const iconMap: { [key: string]: React.ReactNode } = {
			"Accessories & Spares": <Settings color="white" size={24} />,
			"Pressure & Steam Cleaners": <Droplets color="white" size={24} />,
			Vacuums: <Fan color="white" size={24} />,
			"Drying Equipment": <Wind color="white" size={24} />,
			"Floor Scrubbers": <Brush color="white" size={24} />,
			"Buffers & Floor Strippers": <RotateCw color="white" size={24} />,
			"Floor Sweepers": <Brush color="white" size={24} />,
			Transportation: <Truck color="white" size={24} />,
			Carpet: <Home color="white" size={24} />,
			"Outdoor Maintenance": <Trees color="white" size={24} />,
		};

		return iconMap[categoryName] || <Settings color="white" size={24} />;
	};

	const getProductCategoryIcon = (categoryName: string) => {
		const iconMap: { [key: string]: React.ReactNode } = {
			"Trollies & Carts": <Truck color="white" size={24} />,
			Chemicals: <FlaskConical color="white" size={24} />,
			"Industrial Packaging": <Package color="white" size={24} />,
			Washroom: <Droplets color="white" size={24} />,
			"Safety & PPE": <Shield color="white" size={24} />,
			"Waste Management": <Trash2 color="white" size={24} />,
			"Food Service": <UtensilsCrossed color="white" size={24} />,
			"General Cleaning": <Sparkles color="white" size={24} />,
		};

		return iconMap[categoryName] || <Settings color="white" size={24} />;
	};

	const getServiceCategoryIcon = (serviceName: string) => {
		const iconMap: { [key: string]: React.ReactNode } = {
			Mobilisations: <MapPin color="white" size={24} />,
			"Service & Maintenance": <Wrench color="white" size={24} />,
			"Training & Technical Support": <GraduationCap color="white" size={24} />,
			Delivery: <Truck color="white" size={24} />,
		};

		return iconMap[serviceName] || <Settings color="white" size={24} />;
	};

	const getSustainabilityIcon = (sustainabilityName: string) => {
		const iconMap: { [key: string]: React.ReactNode } = {
			Environmental: <Leaf color="white" size={24} />,
			"Social Initiatives": <Users color="white" size={24} />,
			Governance: <Building color="white" size={24} />,
			"Certifications and Ratings": <Award color="white" size={24} />,
		};

		return iconMap[sustainabilityName] || <Settings color="white" size={24} />;
	};

	const getAboutUsIcon = (name: string) => {
		const iconMap: { [key: string]: React.ReactNode } = {
			"Path 2 Partnership Program": <Handshake color="white" size={24} />,
		};

		return iconMap[name] || <Settings color="white" size={24} />;
	};

	const getIndustryIcon = (industryName: string) => {
		const iconMap: { [key: string]: React.ReactNode } = {
			"Aged Care": <Heart color="white" size={24} />,
			Education: <School color="white" size={24} />,
			Gyms: <Dumbbell color="white" size={24} />,
			Healthcare: <Hospital color="white" size={24} />,
			Hospitality: <UtensilsCrossed color="white" size={24} />,
			Mining: <Pickaxe color="white" size={24} />,
			"Shopping Centres": <Store color="white" size={24} />,
			Stadiums: <Trophy color="white" size={24} />,
			Transport: <Plane color="white" size={24} />,
		};

		return iconMap[industryName] || <Settings color="white" size={24} />;
	};

	const OrdersMenu = () => {
		return (
			<Box gap={16}>
				<Typography.H3 color="$basicwhite" weight="bold">
					Recent Orders
				</Typography.H3>
				{(isLoadingOrderData || isFetchingOrderData) && (
					<Box alignSelf="flex-start">
						<ActivityIndicator color="white" size="large" />
					</Box>
				)}
				<Box gap={16} pb={24}>
					{orderData?.list &&
						orderData.list.map((order: any) => {
							let url = `/orders/${order.documentNumber}`;
							if (order.type == "cart") {
								url = `/cart/${order.id}`;
							}
							return (
								<MenuLink key={order.id} href={url}>
									<HStack gap={8} alignItems="center">
										<ShoppingCart color={"white"} />
										<Typography underline color="$basicwhite">
											{order.documentNumber} - {order.siteName ? order.siteName : "No Site"}
										</Typography>
									</HStack>
								</MenuLink>
							);
						})}
				</Box>
				<Box>
					<MenuLink variant="secondary" href={"/orders"}>
						<HStack gap={8}>
							<Typography color="$basicblack" weight="bold">
								Browse all orders
							</Typography>
							<ArrowRight color="black" />
						</HStack>
					</MenuLink>
				</Box>
				{/* {orderData?.map((order) => (
						<Box key={order.id}>
							<Typography.H4 color="$basicwhite" weight="bold">
								{order.documentNumber}
							</Typography.H4>
						</Box>
					))} */}
			</Box>
		);
	};

	const SitesMenu = () => {
		return (
			<Box gap={16}>
				<Typography.H3 color="$basicwhite" weight="bold">
					Recent Sites
				</Typography.H3>
				{(isLoadingSiteData || isFetchingSiteData) && (
					<Box alignSelf="flex-start">
						<ActivityIndicator color="white" size="large" />
					</Box>
				)}
				<Box pb={24} gap={16} flex={1}>
					{siteData?.list &&
						siteData.list.map((site: any) => {
							return (
								<MenuLink key={site.id} href={`/sites/${site.id}`}>
									<MemoizedSiteItem data={site} isMegaMenu />
								</MenuLink>
							);
						})}
				</Box>
				<Box>
					<MenuLink variant="secondary" href={"/sites"}>
						<HStack gap={8}>
							<Typography color="$basicblack" weight="bold">
								Browse all sites
							</Typography>
							<ArrowRight color="black" />
						</HStack>
					</MenuLink>
				</Box>
			</Box>
		);
	};

	const ProductsMenu = () => {
		return (
			<Box padding={6} flex={4} gap={16} borderRadius={8}>
				<HStack gap={24} alignItems="flex-start" borderRadius={8}>
					{(() => {
						const itemsPerColumn = Math.ceil((filteredProductsCategoryData?.length || 0) / 4);
						const columns = [];

						for (let i = 0; i < 4; i++) {
							const startIndex = i * itemsPerColumn;
							const endIndex = startIndex + itemsPerColumn;
							const columnItems = filteredProductsCategoryData?.slice(startIndex, endIndex) || [];

							columns.push(
								<VStack key={i} gap={16} flex={1}>
									{columnItems.map((item: any, index: number) => (
										<MenuLink key={`${i}-${index}`} href={item.url}>
											<HStack gap={8} alignItems="center">
												{getProductCategoryIcon(item.name)}
												<Typography color="$basicwhite" weight="bold">
													{item.name}
												</Typography>
												<Box ml="auto">
													<ChevronRight color="white" size={16} />
												</Box>
											</HStack>
										</MenuLink>
									))}
								</VStack>,
							);
						}

						return columns;
					})()}
				</HStack>
			</Box>
		);
	};

	const ServicesMenu = () => {
		return (
			<Box padding={6} flex={4} gap={16} borderRadius={8}>
				<VStack gap={16}>
					{servicesNavigationItems.map((item, index) => {
						return (
							<Box key={index}>
								<MenuLink href={item.href}>
									<HStack gap={8} alignItems="center">
										{getServiceCategoryIcon(item.name)}
										<Typography.H4 color="$basicwhite" weight="bold">
											{item.name}
										</Typography.H4>
										<Box ml="auto">
											<ChevronRight color="white" size={16} />
										</Box>
									</HStack>
								</MenuLink>
							</Box>
						);
					})}
				</VStack>
			</Box>
		);
	};

	const EquipmentMenu = () => {
		return (
			<HStack gap={48}>
				<Box flex={1} borderRadius={8}>
					<Box>
						<VStack gap={8}>
							{isSignedIn && (
								<>
									<Box flex={1}>
										<MenuLink style={{ width: "$full" }} href={"/equipment/my-equipment"}>
											<HStack gap={24} width={"$full"} p={12} bg="$primarylight" borderRadius={8} alignItems="center">
												<Box>
													<HStack gap={12} alignItems="center">
														<Settings color="white" size={24} />
														<VStack>
															<Typography.H5 color="$basicwhite" weight="bold">
																My Equipment
															</Typography.H5>
														</VStack>
													</HStack>
												</Box>
												<Box ml="auto">
													<ChevronRight color="white" size={24} />
												</Box>
											</HStack>
										</MenuLink>
									</Box>
									<Box>
										<MenuLink style={{ width: "$full" }} href={"/equipment/service-orders"}>
											<HStack width={"$full"} p={12} bg="$primarylight" borderRadius={8} gap={24} alignItems="center">
												<Box>
													<HStack gap={12} alignItems="center">
														<Calendar color="white" size={24} />
														<VStack>
															<Typography.H5 color="$basicwhite" weight="bold">
																Service bookings
															</Typography.H5>
														</VStack>
													</HStack>
												</Box>
												<Box ml="auto">
													<ChevronRight color="white" size={24} />
												</Box>
											</HStack>
										</MenuLink>
									</Box>
								</>
							)}
							<Box>
								<MenuLink style={{ width: "$full" }} href={"/equipment-hire"}>
									<HStack width={"$full"} p={12} bg="$primarylight" borderRadius={8} gap={24} alignItems="center">
										<Box>
											<HStack gap={12} alignItems="center">
												<Truck color="white" size={24} />
												<VStack>
													<Typography.H5 color="$basicwhite" weight="bold">
														Equipment Hire
													</Typography.H5>
												</VStack>
											</HStack>
										</Box>
										<Box ml="auto">
											<ChevronRight color="white" size={24} />
										</Box>
									</HStack>
								</MenuLink>
							</Box>
							<Box>
								<MenuLink style={{ width: "$full" }} href={"/equipment-fincance"}>
									<HStack width={"$full"} p={12} bg="$primarylight" borderRadius={8} gap={24} alignItems="center">
										<Box>
											<HStack gap={12} alignItems="center">
												<Wrench color="white" size={24} />
												<VStack>
													<Typography.H5 color="$basicwhite" weight="bold">
														Equipment Finance
													</Typography.H5>
												</VStack>
											</HStack>
										</Box>
										<Box ml="auto">
											<ChevronRight color="white" size={24} />
										</Box>
									</HStack>
								</MenuLink>
							</Box>
						</VStack>
					</Box>
				</Box>

				<Box padding={6} flex={4} gap={16} borderRadius={8}>
					<HStack gap={24} alignItems="flex-start" borderRadius={8}>
						{(() => {
							const itemsPerColumn = Math.ceil((filteredEquipmentCategoryData?.length || 0) / 3);
							const columns = [];

							for (let i = 0; i < 3; i++) {
								const startIndex = i * itemsPerColumn;
								const endIndex = startIndex + itemsPerColumn;
								const columnItems = filteredEquipmentCategoryData?.slice(startIndex, endIndex) || [];

								columns.push(
									<VStack key={i} gap={16} flex={1}>
										{columnItems.map((item: any, index: number) => (
											<MenuLink key={`${i}-${index}`} href={item.url}>
												<HStack gap={8} alignItems="center">
													{getEquipmentCategoryIcon(item.name)}
													<Typography color="$basicwhite" weight="bold">
														{item.name}
													</Typography>
													<Box ml="auto">
														<ChevronRight color="white" size={16} />
													</Box>
												</HStack>
											</MenuLink>
										))}
									</VStack>,
								);
							}

							return columns;
						})()}
					</HStack>
				</Box>
			</HStack>
		);
	};

	const SustainabilityMenu = () => {
		return (
			<Box padding={6} flex={4} gap={16} borderRadius={8}>
				<VStack gap={16}>
					{sustainabilityNavigationItems.map((item, index) => {
						return (
							<Box key={index}>
								<MenuLink href={item.href}>
									<HStack gap={8} alignItems="center">
										{getSustainabilityIcon(item.name)}
										<Typography.H4 color="$basicwhite" weight="bold">
											{item.name}
										</Typography.H4>
										<Box ml="auto">
											<ChevronRight color="white" size={16} />
										</Box>
									</HStack>
								</MenuLink>
							</Box>
						);
					})}
				</VStack>
			</Box>
		);
	};

	const AboutUsMenu = () => {
		const industriesList = aboutUsNavigationItems.filter((item) => item.name !== "Path 2 Partnership Program" && item.name !== "Careers");
		return (
			<HStack gap={48}>
				<Box flex={2} borderRadius={8}>
					<Box>
						<VStack gap={12}>
							<VStack gap={4} flex={1}>
								<MenuLink style={{ width: "$full" }} href={"/about-us/path-2-partnership"}>
									<HStack gap={24} width={"$full"} p={12} bg="$primarylight" borderRadius={8} alignItems="center">
										<Box>
											<HStack gap={12} alignItems="center">
												{getAboutUsIcon("Path 2 Partnership Program")}
												<VStack>
													<Typography.H5 color="$basicwhite" weight="bold">
														Path 2 Partnership Program
													</Typography.H5>
												</VStack>
											</HStack>
										</Box>
										<Box ml="auto">
											<ChevronRight color="white" size={24} />
										</Box>
									</HStack>
								</MenuLink>
								<MenuLink style={{ width: "$full" }} href={"/about-us/careers"}>
									<HStack gap={24} width={"$full"} p={12} bg="$primarylight" borderRadius={8} alignItems="center">
										<Box>
											<HStack gap={12} alignItems="center">
												<GraduationCap color="white" size={24} />
												<VStack>
													<Typography.H5 color="$basicwhite" weight="bold">
														Careers
													</Typography.H5>
												</VStack>
											</HStack>
										</Box>
										<Box ml="auto">
											<ChevronRight color="white" size={24} />
										</Box>
									</HStack>
								</MenuLink>
							</VStack>
						</VStack>
					</Box>
				</Box>

				<Box padding={6} flex={5} gap={16} borderRadius={8}>
					<HStack gap={24} alignItems="flex-start" borderRadius={8}>
						{(() => {
							const itemsPerColumn = Math.ceil(industriesList.length / 3);
							const columns = [];

							for (let i = 0; i < 3; i++) {
								const startIndex = i * itemsPerColumn;
								const endIndex = startIndex + itemsPerColumn;
								const columnItems = industriesList.slice(startIndex, endIndex);

								columns.push(
									<VStack key={i} gap={16} flex={1}>
										{columnItems.map((item: any, index: number) => (
											<MenuLink key={`${i}-${index}`} href={item.href}>
												<HStack gap={8} alignItems="center">
													{getIndustryIcon(item.name)}
													<Typography color="$basicwhite" weight="bold">
														{item.name}
													</Typography>
													<Box ml="auto">
														<ChevronRight color="white" size={16} />
													</Box>
												</HStack>
											</MenuLink>
										))}
									</VStack>,
								);
							}

							return columns;
						})()}
					</HStack>
				</Box>
			</HStack>
		);
	};

	const ArticlesMenu = () => {
		return (
			<Box gap={12}>
				<Typography.H4 color="$basicwhite" weight="bold">
					Recent Articles
				</Typography.H4>
				{(isLoadingArticleData || isFetchingArticleData) && (
					<Box alignSelf="flex-start">
						<ActivityIndicator color="white" size="large" />
					</Box>
				)}
				<Box pb={24} gap={12} flex={1}>
					{articleData?.list &&
						articleData.list.map((article: any) => {
							return (
								<MenuLink href={`/articles/${article.slug}`} key={article.slug}>
									<HStack gap={8}>
										<ScrollText color="white" />
										<Typography sx={{ textDecorationLine: "underline" }} color="$basicwhite">
											{article.pageName}
										</Typography>
									</HStack>
								</MenuLink>
							);
						})}
				</Box>
				<Box>
					<MenuLink href={"/articles"}>
						<HStack gap={8}>
							<Typography sx={{ textDecorationLine: "underline" }} color="$basicwhite" weight="bold">
								Browse all articles
							</Typography>
							<ArrowRight color="white" />
						</HStack>
					</MenuLink>
				</Box>
			</Box>
		);
	};

	if (hoveredNavItem === "/sites" && isSignedIn) {
		content = <SitesMenu />;
	}

	if (hoveredNavItem === "/orders" && isSignedIn) {
		content = <OrdersMenu />;
	}
	if (hoveredNavItem === "/products") {
		content = <ProductsMenu />;
	}
	if (hoveredNavItem === "/equipment") {
		content = <EquipmentMenu />;
	}
	if (hoveredNavItem === "/services") {
		content = <ServicesMenu />;
	}
	if (hoveredNavItem === "/sustainability") {
		content = <SustainabilityMenu />;
	}
	if (hoveredNavItem === "/about-us") {
		content = <AboutUsMenu />;
	}
	if (hoveredNavItem === "/articles") {
		content = <ArticlesMenu />;
	}

	return <Box>{content}</Box>;
};

export default MegaMenuContent;
